import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useModalLayer } from '../ui';
import { AgentPanel } from './AgentPanel';
import { useAgentStatus, useAgentUi } from './useAgent';

const WIDE = '(min-width: 1100px)';
function useWide() {
  const get = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(WIDE).matches;
  const [wide, setWide] = useState(get);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(WIDE);
    const on = () => setWide(mq.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return wide;
}

/** Open state of the panel for the shell (third grid column on wide screens). */
export const useAgentColumn = () => {
  const open = useAgentUi((s) => s.open);
  return useWide() && open;
};

/**
 * Where the assistant lives in the landlord app: a column on the right on wide screens, a sheet over the page on
 * smaller ones (focus trapped, Escape closes), a round "KI" button when closed.
 */
export function AgentHost() {
  const { t } = useTranslation();
  const wide = useWide();
  const open = useAgentUi((s) => s.open);
  const setOpen = useAgentUi((s) => s.setOpen);
  const status = useAgentStatus();
  const sheet = useRef<HTMLDivElement>(null);
  useModalLayer(open && !wide, sheet, () => setOpen(false));
  // keyboard: Ctrl/Cmd + J toggles the assistant
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'j') {
        e.preventDefault();
        setOpen(!useAgentUi.getState().open);
      }
    };
    window.addEventListener('keydown', on);
    return () => window.removeEventListener('keydown', on);
  }, [setOpen]);
  if (status.data && !status.data.configured) return null;
  if (!open)
    return (
      <button type="button" className="agent-launcher" aria-label={t('agent.open')} title={t('agent.open')} onClick={() => setOpen(true)}>
        <span aria-hidden>{t('agent.badge')}</span>
      </button>
    );
  if (wide) return <AgentPanel mobile={false} />;
  return (
    <div className="agent-sheet" onMouseDown={(e) => e.target === e.currentTarget && setOpen(false)}>
      <div ref={sheet} className="agent-sheet-panel" role="dialog" aria-modal="true" aria-label={t('agent.name')} tabIndex={-1}>
        <AgentPanel mobile />
      </div>
    </div>
  );
}
